import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '../../components/primitives';
import { useAuth } from './AuthContext';
import { AuthLayout } from './AuthLayout';
import { ApiError } from '../../lib/api';
import styles from './AuthPages.module.css';

export function LoginPage() {
  const { login, reactivate } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Účet je deaktivovaný → nabídni obnovu (stejný e-mail + heslo, admin netřeba).
  const [deactivated, setDeactivated] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/app';

  // Odkaz z e-mailu pro obnovu hesla (?reset_token=…) přesměruj na reset formulář.
  const resetToken = searchParams.get('reset_token');
  if (resetToken) {
    return <Navigate to={`/reset-password?token=${encodeURIComponent(resetToken)}`} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDeactivated(false);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setDeactivated(true);
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Nesprávné přihlašovací údaje.');
      } else if (err instanceof ApiError) {
        setError(`Chyba ${err.status}: přihlášení se nezdařilo.`);
      } else {
        setError('Nelze se spojit se serverem.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReactivate() {
    setError(null);
    setSubmitting(true);
    try {
      await reactivate(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Chyba ${err.status}: účet se nepodařilo obnovit.`
          : 'Nelze se spojit se serverem.',
      );
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout subtitle="Přihlaste se pro pokračování">
      <Card>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          {deactivated && (
            <div className={styles.notice} role="alert">
              <p>
                Tento účet je deaktivovaný. Vaše data jsou stále uložená — účet můžete obnovit a
                pokračovat tam, kde jste skončili.
              </p>
              <Button type="button" variant="primary" fullWidth disabled={submitting} onClick={handleReactivate}>
                {submitting ? 'Obnovuji…' : 'Obnovit účet a přihlásit se'}
              </Button>
            </div>
          )}

          <Input
            label="E‑mail"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vas@email.cz"
          />

          <Input
            label="Heslo"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />

          <div className={styles.links}>
            <Link to="/forgot-password" className={styles.link}>
              Zapomenuté heslo?
            </Link>
          </div>

          <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
            {submitting ? 'Přihlašuji…' : 'Přihlásit se'}
          </Button>

          <div className={styles.switch}>
            Nemáte účet?{' '}
            <Link to="/register" className={styles.link}>
              Zaregistrujte se zdarma
            </Link>
          </div>
        </form>
      </Card>
    </AuthLayout>
  );
}
